// src/pages/admin/library/AddCategoryModal.tsx
import React, { useEffect, useState } from 'react';
import { FaTimes, FaSave, FaPalette } from '../../../utils/iconUtils';

interface AddCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (category: { name: string;  color?: string }) => void;
  initialData?: Partial<CategoryForm>;
}

interface CategoryForm {
  name: string;
  description: string;
  color: string;
}

const AddCategoryModal: React.FC<AddCategoryModalProps> = ({ isOpen, onClose, onSave, initialData }) => {
  const [formData, setFormData] = useState<CategoryForm>({
    name: '',
    description: '',
    color: '#3b82f6' // Default blue
  });

  const presetColors = [
    '#3b82f6', // Blue
    '#10b981', // Green
    '#8b5cf6', // Purple
    '#f59e0b', // Yellow
    '#ef4444', // Red
    '#06b6d4', // Cyan
    '#84cc16', // Lime
    '#f97316', // Orange
    '#6366f1', // Indigo
    '#ec4899', // Pink
  ];

  useEffect(() => {
    if (isOpen && initialData) {
      setFormData(prev => ({
        ...prev,
        name: initialData.name || '',
        description: initialData.description || '',
        color: initialData.color || '#3b82f6'
      }));
    } else if (isOpen) {
      // Reset form when opening for new category
      setFormData({
        name: '',
        description: '',
        color: '#3b82f6'
      });
    }
  }, [isOpen, initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleColorChange = (color: string) => {
    setFormData(prev => ({ ...prev, color }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Category name is required');
      return;
    }
    
    onSave({
      name: formData.name.trim(),
      color: formData.color
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-1">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
        
        <div className="relative bg-white rounded shadow w-full max-w-sm">
          <div className="bg-primary-600 px-3 py-2 flex justify-between items-center">
            <h3 className="text-xs font-semibold text-white">
              {initialData ? 'Edit Category' : 'Add New Category'}
            </h3>
            <button onClick={onClose} className="text-white hover:text-gray-200">
              <FaTimes className="h-3 w-3" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-3">
            <div className="space-y-2">
              {/* Category Name */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Category Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Academic, Fiction, Science"
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                  autoFocus
                />
              </div>

          

              {/* Color Picker */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  <FaPalette className="inline mr-1" />
                  Color
                </label>
                
                {/* Selected Color Preview */}
                <div className="flex items-center mb-2">
                  <div 
                    className="w-5 h-5 rounded border border-gray-300 mr-2"
                    style={{ backgroundColor: formData.color }}
                  />
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => handleColorChange(e.target.value)}
                    className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded font-mono"
                    placeholder="#3b82f6"
                  />
                </div>

                {/* Preset Colors */}
                <div className="grid grid-cols-6 gap-1">
                  {presetColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => handleColorChange(color)}
                      className={`w-4 h-4 rounded-full border ${
                        formData.color === color 
                          ? 'border-gray-800 scale-105 ring-1' 
                          : 'border-gray-300 hover:border-gray-400'
                      } transition-transform`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-3 flex justify-end gap-2">
              <button 
                type="button" 
                onClick={onClose}
                className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button 
                type="submit"
                className="flex items-center gap-2 px-3 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
              >
                <FaSave className="h-3 w-3" />
                {initialData ? 'Update' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddCategoryModal;